"use client";

import { api } from "@v1/backend/convex/_generated/api";
import { useQuery } from "convex/react";
import { useMemo, useState } from "react";
import { readVisitorChatTokens, visitorChatScope } from "../customer-chat/customer-chat-session-storage";
import type { MessengerVisitorChannel } from "./messenger-context";
import { visitorChatView } from "./messenger-types";
import type { MessengerConversation } from "./use-messenger-conversations";

/** The anonymous visitor's chats from this browser's tokens, newest first; undefined while loading. */
export function useMessengerVisitorChats(channel: MessengerVisitorChannel): readonly MessengerConversation[] | undefined {
  // Read once per mount: the start view mounts again after every chat.
  const [tokens] = useState(() => readVisitorChatTokens(visitorChatScope(channel)));
  const any = tokens.length > 0;
  const fromHelp = useQuery(
    api.customerChat.public_widget.listVisitorChats,
    any && channel.kind === "help_center" ? { org_slug: channel.orgSlug, visitor_tokens: tokens } : "skip",
  );
  const fromSite = useQuery(
    api.customerChat.embed_widget.listVisitorChats,
    any && channel.kind === "website" ? { site_key: channel.siteKey, visitor_tokens: tokens } : "skip",
  );
  const rows = channel.kind === "website" ? fromSite : fromHelp;
  return useMemo((): readonly MessengerConversation[] | undefined => {
    if (!any) return [];
    return rows?.map((row) => ({
      key: `visitor:${row.visitor_token}`,
      title: row.title,
      preview: row.preview,
      at: row.last_message_at,
      unread: false,
      team: row.team,
      status: null,
      channel: null,
      open: visitorChatView(row.visitor_token),
      taskId: null,
    }));
  }, [any, rows]);
}
